// What a person has chosen, kept in this browser: the look of the board, the
// filters on Train, the level the bot plays at. One object, shared by every
// screen, React or not; a change is saved and announced.

export interface Prefs {
  theme: "system" | "light" | "dark";
  board: string;
  pieces: string;
  strictness: "strict" | "normal" | "loose";
  trainKind: string;
  trainSpeed: string;
  /** One motif to train, from the "what you miss" page; "" is all of them. */
  trainMotif: string;
  gamesSpeed: string;
  gamesAll: boolean;
  /** "" follows the level the server reads off your games. */
  band: string;
  /** The level the bot was last asked to play at; "" is your own. */
  sparLevel: string;
  /** Whether leaving your line in a game makes a noise as well as a scene. */
  sparSound: boolean;
  /** The whole tree of a repertoire's moves beside its board: for strong
   *  players. Everyone else walks a line from the board, a move at a time. */
  repTree: boolean;
  /** Which of the repertoire page's book tables was open last. */
  repBook?: boolean;
}

const KEY = "blundermine.prefs";

function stored(): Partial<Prefs> {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}") as Partial<Prefs>;
  } catch {
    return {};
  }
}

export const prefs: Prefs = Object.assign(
  {
    theme: "system",
    board: "default",
    pieces: "standard",
    strictness: "normal",
    trainKind: "",
    trainSpeed: "",
    trainMotif: "",
    gamesSpeed: "all",
    gamesAll: false,
    band: "",
    sparLevel: "",
    sparSound: true,
    repTree: false,
  } satisfies Prefs,
  stored()
);

const listeners = new Set<() => void>();
let version = 0;

export function savePrefs(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // A private window: the choice lasts as long as the page.
  }
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribePrefs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const prefsVersion = (): number => version;

// --- THE THEME ---

const lightQuery = matchMedia("(prefers-color-scheme: light)");

export function effectiveTheme(): "light" | "dark" {
  if (prefs.theme === "light" || prefs.theme === "dark") return prefs.theme;
  return lightQuery.matches ? "light" : "dark";
}

export function applyTheme(): void {
  const theme = effectiveTheme();
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === "light" ? "#f4f3ef" : "#0f1013";
}
lightQuery.addEventListener("change", applyTheme);

/** How sure a position has to be before it counts, as server config overrides. */
export const STRICTNESS: Record<string, Record<string, number> | null> = {
  // 0.70 is Lichess's bar for a public puzzle database: nothing but the best
  // move may work at all.
  strict: { uniqueness: 0.7, decisive_cp: 300, blunder_drop: 0.35 },
  normal: null,
  loose: { uniqueness: 0.2, decisive_cp: 150, blunder_drop: 0.22, already_winning_cp: 600 },
};
